import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { SportsAcademyFormData } from '@/types';
import { sportsApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { ImageUploadField } from '@/components/forms/ImageUploadField';
import { FreeOfferingField } from '@/components/forms/FreeOfferingField';
import { TagInput } from '@/components/ui/TagInput';
import { CategorySelectField } from '@/components/forms/CategorySelectField';
import { VideoUploadField } from '@/components/forms/VideoUploadField';

const audienceSuggestions = [
  'Kids (Age 7-12)',
  'Open to All',
  'Teens',
  'College Students',
  'Professionals',
];

function parseCommaSeparated(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseLineSeparated(value: string) {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

export const AdminSportsForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SportsAcademyFormData>({
    defaultValues: {
      categoryId: '',
      name: '',
      city: '',
      area: '',
      audience: [],
      level: 'Open to All',
      teachingMode: 'Offline',
      sportsText: '',
      coverImageKey: '',
      previewVideoKey: '',
      startingPriceText: '',
      isFree: false,
      highlightsText: '',
      about: '',
      facilitiesText: '',
      programs: [],
    },
  });
  const coverImageKey = watch('coverImageKey');
  const previewVideoKey = watch('previewVideoKey');
  const isFree = watch('isFree');

  const { fields: programs, append: appendProgram, remove: removeProgram } = useFieldArray({
    control,
    name: 'programs',
  });

  useEffect(() => {
    if (!isEditMode || !id) {
      setCoverPreviewUrl('');
      setVideoPreviewUrl('');
      return;
    }

    const controller = new AbortController();

    const fetchAcademy = async () => {
      try {
        const academy = await sportsApi.getSportsById(id, controller.signal);
        if (!academy) return;

        reset({
          categoryId: academy.categoryId || '',
          name: academy.name,
          city: academy.city,
          area: academy.area ?? '',
          audience: academy.audience || [],
          level: academy.level || 'Open to All',
          teachingMode: academy.teachingMode || 'Offline',
          sportsText: (academy.sports || []).join(', '),
          coverImageKey: academy.coverImageKey,
          previewVideoKey: academy.previewVideoKey || '',
          startingPriceText: academy.startingPriceText,
          isFree: academy.isFree ?? false,
          highlightsText: (academy.highlights || []).join('\n'),
          about: academy.about,
          facilitiesText: (academy.facilities || []).join('\n'),
          programs: academy.programs || [],
        });
        setCoverPreviewUrl(academy.coverImageUrl || '');
        setVideoPreviewUrl(academy.previewVideoUrl || '');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch sports academy:', error);
        }
      }
    };

    void fetchAcademy();
    return () => controller.abort();
  }, [id, isEditMode, reset]);

  const onSubmit = async (data: SportsAcademyFormData) => {
    setSubmitError('');
    setIsLoading(true);

    const normalizedPrograms = (data.programs || [])
      .map((program) => ({
        name: String(program.name || '').trim(),
        schedule: String(program.schedule || '').trim(),
        feeText: String(program.feeText || '').trim(),
        ageGroup: String(program.ageGroup || '').trim(),
      }))
      .filter((program) => program.name && program.schedule && program.feeText)
      .map((program) => ({
        name: program.name,
        schedule: program.schedule,
        feeText: program.feeText,
        ageGroup: program.ageGroup || undefined,
      }));

    const payload = {
      categoryId: data.categoryId,
      name: data.name,
      city: data.city,
      area: data.area,
      audience: data.audience,
      level: data.level,
      teachingMode: data.teachingMode,
      sports: parseCommaSeparated(data.sportsText),
      coverImageKey: data.coverImageKey,
      previewVideoKey: data.previewVideoKey,
      startingPriceText: data.startingPriceText,
      isFree: Boolean(data.isFree),
      highlights: parseLineSeparated(data.highlightsText),
      about: data.about,
      facilities: parseLineSeparated(data.facilitiesText),
      programs: normalizedPrograms,
    };

    try {
      if (isEditMode && id) {
        await sportsApi.updateSports(id, payload);
      } else {
        await sportsApi.createSports(payload);
      }

      navigate('/admin/sports');
    } catch (error) {
      setSubmitError((error as Error).message || 'Failed to save sports academy.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="page-heading">{isEditMode ? 'Edit Sports Academy' : 'Create Sports Academy'}</h1>
        <p className="page-subtitle">
          Build polished sports listings with clear audience, level, mode, and program details for families to review.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <div className="grid gap-5 md:grid-cols-2">
            <Input
              label="Academy Name"
              placeholder="e.g., Stride Cricket Academy"
              {...register('name', { required: 'Name is required' })}
              error={errors.name?.message}
            />

            <Input
              label="Sports"
              placeholder="Cricket, Badminton"
              {...register('sportsText', { required: 'At least one sport is required' })}
              error={errors.sportsText?.message}
            />

            <Controller
              control={control}
              name="categoryId"
              rules={{ required: 'Please select a category' }}
              render={({ field }) => (
                <CategorySelectField
                  type="sports"
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={errors.categoryId?.message}
                />
              )}
            />

            <Input
              label="City"
              placeholder="e.g., Chennai"
              {...register('city', { required: 'City is required' })}
              error={errors.city?.message}
            />

            <Input label="Area" placeholder="e.g., Velachery" {...register('area')} />

            <div>
              <label className="form-label">Level</label>
              <Input placeholder="Open to All" {...register('level', { required: 'Level is required' })} error={errors.level?.message} />
            </div>

            <div>
              <label className="form-label">Teaching Mode</label>
              <select className="form-control min-h-11" {...register('teachingMode', { required: true })}>
                <option value="Offline">Offline</option>
                <option value="Online">Online</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <Controller
                control={control}
                name="audience"
                rules={{ validate: (value) => value.length > 0 || 'Add at least one audience segment' }}
                render={({ field }) => (
                  <TagInput
                    label="Audience"
                    values={field.value || []}
                    onChange={field.onChange}
                    suggestions={audienceSuggestions}
                    placeholder="Add audience groups"
                    error={errors.audience?.message}
                    helperText="Audience tags are highlighted on the public cards and detail page."
                  />
                )}
              />
            </div>

            <input type="hidden" {...register('coverImageKey', { required: 'Sports cover image is required' })} />
            <div className="md:col-span-2">
              <ImageUploadField
                label="Cover Image"
                folder="banners"
                imageKey={coverImageKey || ''}
                previewUrl={coverPreviewUrl}
                error={errors.coverImageKey?.message}
                helperText="Use a banner-style image for both listing cards and the detail hero section."
                onChange={({ key, url }) => {
                  setValue('coverImageKey', key, { shouldValidate: true, shouldDirty: true });
                  setCoverPreviewUrl(url);
                }}
              />
            </div>

            <input type="hidden" {...register('previewVideoKey')} />
            <div className="md:col-span-2">
              <VideoUploadField
                folder="sports-videos"
                videoKey={previewVideoKey || ''}
                previewUrl={videoPreviewUrl}
                onChange={({ key, url }) => {
                  setValue('previewVideoKey', key, { shouldDirty: true });
                  setVideoPreviewUrl(url);
                }}
              />
            </div>

            <Input
              label="Starting Price Text"
              placeholder="From Rs 1,999 / month"
              {...register('startingPriceText', { required: 'Starting price text is required' })}
              error={errors.startingPriceText?.message}
            />

            <div className="md:col-span-2">
              <FreeOfferingField
                checked={Boolean(isFree)}
                onChange={(checked) => setValue('isFree', checked, { shouldDirty: true })}
                itemLabel="sports listing"
              />
            </div>

            <div className="md:col-span-2">
              <label className="form-label">About</label>
              <textarea
                className="form-control min-h-[140px]"
                placeholder="Describe the academy, coaching style, and what makes it trustworthy."
                {...register('about', { required: 'About section is required' })}
              />
              {errors.about ? <p className="mt-1.5 text-sm font-medium text-red-600">{errors.about.message}</p> : null}
            </div>

            <div>
              <label className="form-label">Highlights</label>
              <textarea
                className="form-control min-h-[120px]"
                placeholder="Certified coaches&#10;Weekend batches"
                {...register('highlightsText')}
              />
            </div>

            <div>
              <label className="form-label">Facilities</label>
              <textarea
                className="form-control min-h-[120px]"
                placeholder="Turf nets&#10;Fitness sessions"
                {...register('facilitiesText')}
              />
            </div>
          </div>
        </Card>

        <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Programs and Batches</h2>
              <p className="mt-1 text-sm text-slate-600">Add the key batch options that families can compare.</p>
            </div>
            <Button type="button" variant="secondary" onClick={() => appendProgram({ name: '', schedule: '', feeText: '', ageGroup: '' })}>
              <Plus className="h-4 w-4" />
              Add Program
            </Button>
          </div>

          {programs.length === 0 ? (
            <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center text-sm text-slate-600">
              No programs added yet.
            </div>
          ) : (
            <div className="space-y-4">
              {programs.map((program, index) => (
                <div key={program.id} className="form-panel">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input label="Program Name" placeholder="Beginners Cricket" {...register(`programs.${index}.name` as const)} />
                    <Input label="Schedule" placeholder="Mon/Wed/Fri - 6:00 PM" {...register(`programs.${index}.schedule` as const)} />
                    <Input label="Fee Text" placeholder="Rs 1,999 / month" {...register(`programs.${index}.feeText` as const)} />
                    <Input label="Age Group" placeholder="8-14" {...register(`programs.${index}.ageGroup` as const)} />
                  </div>

                  <div className="mt-4 flex justify-end">
                    <Button type="button" variant="danger" size="sm" onClick={() => removeProgram(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {submitError ? <p className="status-message border-red-100 bg-red-50 text-red-700">{submitError}</p> : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/sports')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : isEditMode ? 'Update Sports Academy' : 'Create Sports Academy'}
          </Button>
        </div>
      </form>
    </div>
  );
};
